import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import baseUrl from "../../api/baseUrl";
import SmsPayButton from "../../components/sms/SmsPayButton";
import AccountPageScroll from "../myaccount/components/AccountPageScroll";

type SmsPackageOption = {
  _id: string;
  name: string;
  tokens: number;
  price: number;
  description?: string;
};

const CHANNEL_LABELS: Record<string, string> = { sms: "SMS", whatsapp: "WhatsApp", email: "email" };

export default function SmsTopUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("channel") || "sms";
  const channel = CHANNEL_LABELS[requested] ? requested : "sms";
  const label = CHANNEL_LABELS[channel];
  const [packages, setPackages] = useState<SmsPackageOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<any>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const loadPackages = useCallback(() => {
    setLoading(true);
    baseUrl
      .get("/client/sms-packages", { params: { channel } })
      .then((res) => {
        if (res?.data?.success) setPackages(res.data.data ?? []);
      })
      .catch(() => setError(`Could not load ${label} packages.`))
      .finally(() => setLoading(false));
  }, [channel, label]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const startCheckout = async (packageId: string) => {
    setSelectedPackageId(packageId);
    setCheckout(null);
    setError(null);
    setCheckoutLoading(true);
    try {
      const res = await baseUrl.post("/client/sms-packages/checkout", { packageId });
      if (res?.data?.success) {
        setCheckout(res.data.data);
      } else {
        setError("Could not start checkout.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Could not start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePaymentEnded = () => {
    setCheckout(null);
    setSelectedPackageId(null);
    setCheckoutLoading(false);
  };

  return (
    <AccountPageScroll className="bg-slate-50 dark:bg-slate-950">
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Back
        </button>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <SmsOutlinedIcon className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Buy {label} tokens</h1>
            <p className="text-sm text-slate-500">Pay with your card via PayHere</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading packages…</p>
        ) : packages.length === 0 ? (
          <p className="rounded-xl border bg-white p-5 text-sm text-slate-600">
            No {label} packages available. Contact your platform admin.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {packages.map((pkg) => (
                <div
                  key={pkg._id}
                  className={`rounded-xl border bg-white p-5 dark:bg-slate-900 ${
                    selectedPackageId === pkg._id ? "border-primary" : "border-slate-200"
                  }`}
                >
                  <p className="font-semibold">{pkg.name}</p>
                  <p className="mt-2 text-2xl font-bold text-primary">
                    {pkg.tokens.toLocaleString()} {label} tokens
                  </p>
                  <p className="mt-1 text-sm text-slate-600">Rs. {pkg.price.toLocaleString()}</p>
                  {pkg.description ? (
                    <p className="mt-2 text-xs text-slate-500">{pkg.description}</p>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => startCheckout(pkg._id)}
                    disabled={checkoutLoading && selectedPackageId === pkg._id}
                    className="mt-4 w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {checkoutLoading && selectedPackageId === pkg._id ? "Preparing…" : "Buy now"}
                  </button>
                </div>
              ))}
            </div>

            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            {checkout ? (
              <div className="flex flex-col gap-3 rounded-xl border bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCartOutlinedIcon className="text-primary" />
                  <div>
                    <p className="font-semibold">{checkout.packageName}</p>
                    <p className="text-sm text-slate-600">
                      {checkout.tokens?.toLocaleString()} tokens — Rs. {checkout.amount}
                    </p>
                  </div>
                </div>
                <SmsPayButton
                  checkout={checkout}
                  disabled={checkoutLoading}
                  onPaymentEnded={handlePaymentEnded}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </AccountPageScroll>
  );
}
